import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getOrderBetween } from '@epam/uui-core';

import { draftTask, uploadFiles } from 'api/task.service';
import { useToast } from 'hooks/ui';
import { Task } from 'models/task.model';
import { useTaskStore } from 'store/task';
import { useApplicationStore } from 'store/application';
import { scrollToItem } from 'hooks/helpers/common';
import { taskRoutes } from 'router/taskRoutes';
import { taskKeys } from './queryKeys';

type HookArgs = {
  keepActive?: boolean;
};

export const useTaskCreateQuery = (props: HookArgs = { keepActive: false }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { assignedGroup, tasks } = useTaskStore((state) => state);
  const { setLoading, removeLoading } = useApplicationStore((state) => state);

  const { isPending, mutate, data } = useMutation({
    mutationFn: (task: Task) => {
      setLoading();

      return draftTask({
        title: task.title,
        description: task.description,
        errorMessage: task.errorMessage,
        successMessage: task.successMessage,
        comment: task.comment,
        validator: task.validator,
        workModels: task.workModels,
        answers: task.answers,
        taskGroupId: assignedGroup,
        order: getOrderBetween(tasks[tasks.length - 1]?.order, null),
      }).then(async (res) => {
        if (task.attachments?.length) {
          await uploadFiles(res.id, task.attachments);
        }

        return res;
      });
    },
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      toast.success('Task has been drafted successfully');

      if (props.keepActive) {
        navigate(taskRoutes.taskId(task.id));
        scrollToItem(task.id);
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => removeLoading(),
  });

  return { isLoading: isPending, mutate, data };
};
